import { MainApi } from 'api/MainApi'
import { RobotApi } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import FavoritesOnlyChip from 'components/FavoritesOnlyChip/FavoritesOnlyChip'
import Loader from 'components/Loader/Loader'
import TablePagination from 'components/TablePagination/TablePagination'
import Title from 'components/Title/Title'
import { useIsMobile } from 'hooks/useIsMobile'
import { useUrlQueryState } from 'hooks/useUrlQueryState'
import { useUser } from 'hooks/user'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import pageFrame from 'styles/pageFrame.module.css'
import { PROGRESS_TYPES } from 'utils/constants'
import { IRobotInfo } from 'utils/interfaces'
import { sortWithFavoritesFirst } from 'utils/favoritesSort'
import { flagParam, pageParam, stringParam } from 'utils/urlQueryState'

import AddFacilityModal from './AddFacilityModal/AddFacilityModal'
import { IFacilityInfo } from './FacilityTable/FacilityItem/FacilityItem'
import FacilityTable from './FacilityTable/FacilityTable'

import style from './FacilityList.module.css'

const PAGE_SIZE = 20

const FacilityList = () => {
  const { t } = useTranslation()
  const user = useUser()
  const isMobile = useIsMobile()
  const [facilities, setFacilities] = useState<IFacilityInfo[]>([])
  const [loadingProgress, setLoadingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [creatingProgress, setCreatingProgress] = useState(PROGRESS_TYPES.IDLE)
  const [url, setUrl] = useUrlQueryState({
    q: stringParam(),
    page: pageParam(),
    fav: flagParam(),
  })
  const page = url.page
  const setPage = (value: number | ((prev: number) => number)) =>
    setUrl(s => ({ page: typeof value === 'function' ? value(s.page) : value }))
  const [totalPages, setTotalPages] = useState(1)
  const searchText = url.q
  const [addFacilityModalOpen, setAddFacilityModalOpen] = useState(false)
  const [robots, setRobots] = useState<IRobotInfo[]>([])

  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const favoriteFacilityIds = useMemo(
    () => new Set(user?.state.favoriteFacilityIds ?? []),
    [user?.state.favoriteFacilityIds]
  )
  const orderedFacilities = useMemo(
    () => sortWithFavoritesFirst(facilities, favoriteFacilityIds),
    [facilities, favoriteFacilityIds]
  )

  const fetchData = useCallback(async () => {
    setLoadingProgress(PROGRESS_TYPES.WORK)
    try {
      const response = await MainApi.fetchFacilities(page, PAGE_SIZE, searchText || undefined, {
        favoritesFirst: true,
        favoritesOnly: url.fav,
      })
      setFacilities(response.results)
      setTotalPages(Math.max(1, Math.ceil(response.count / PAGE_SIZE)))
      setLoadingProgress(PROGRESS_TYPES.SUCCESS)
    } catch (error) {
      console.error('Error fetching facilities:', error)
      setLoadingProgress(PROGRESS_TYPES.ERROR)
    }
  }, [page, searchText, url.fav, user?.state.favoriteFacilityIds])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  useEffect(() => {
    const loadRobots = async () => {
      try {
        const result = await RobotApi.fetchRobots()
        setRobots(result.results)
      } catch (error) {
        console.error('Error fetching robots:', error)
      }
    }
    loadRobots()
  }, [])

  const onSearchChange = (value: string) => {
    setUrl({ q: value })
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current)
    }
    searchTimeoutRef.current = setTimeout(() => {
      setUrl({ q: value, page: 1 })
    }, 300)
  }

  const createFacility = async (data: {
    name: string
    facility_type: string
    street_address?: string
    city?: string
    country?: string
    robots_ids?: number[]
  }) => {
    setCreatingProgress(PROGRESS_TYPES.WORK)
    try {
      await MainApi.createFacility(data)
      setAddFacilityModalOpen(false)
      setCreatingProgress(PROGRESS_TYPES.SUCCESS)
      fetchData()
    } catch (error) {
      console.error('Error creating facility:', error)
      setCreatingProgress(PROGRESS_TYPES.ERROR)
    }
  }

  return (
    <div className={clsx(pageFrame.frame, style.facilities)}>
      <div className={style.headerArea}>
        <div className={style.titleBlock}>
          <Title text="Facilities" />
          <div className={style.headerRight}>
            <Button.Standart
              className={style.button}
              onClick={() => setAddFacilityModalOpen(true)}
              loading={creatingProgress === PROGRESS_TYPES.WORK}
            >
              {'New Facility'}
            </Button.Standart>
          </div>
        </div>
        <div className={style.controlsBlock}>
          <input
            className={style.searchInput}
            value={searchText}
            onChange={e => onSearchChange(e.target.value)}
            placeholder={t('common.search')}
          />
          <FavoritesOnlyChip pressed={url.fav} onClick={() => setUrl({ fav: !url.fav, page: 1 })} />
        </div>
      </div>
      {loadingProgress === PROGRESS_TYPES.WORK && <Loader />}
      {loadingProgress === PROGRESS_TYPES.SUCCESS && (
        <div className={style.tableContainer}>
          <FacilityTable
            facilities={orderedFacilities}
            onFacilitiesUpdated={fetchData}
            robots={robots}
            emptyTitle={url.fav ? t('favorites.empty_facilities') : undefined}
            emptyDescription={url.fav ? t('favorites.empty_facilities_description') : undefined}
          />
          <TablePagination
            page={page}
            totalPages={totalPages}
            pageInput={page.toString()}
            onPageInputChange={val => {
              const p = parseInt(val)
              if (!isNaN(p) && p >= 1 && p <= totalPages) {
                setPage(p)
              }
            }}
            onPageInputKeyDown={e => {
              if (e.key === 'Enter') {
                // Already handled by onPageInputChange for simplicity here,
                // or we could add a separate state for input like in ScenarioList
              }
            }}
            onPageInputBlur={() => {}}
            onPrev={() => setPage(p => Math.max(1, p - 1))}
            onNext={() => setPage(p => Math.min(totalPages, p + 1))}
          />
        </div>
      )}
      <AddFacilityModal
        isOpen={addFacilityModalOpen}
        onClose={() => setAddFacilityModalOpen(false)}
        onSave={createFacility}
        robots={robots}
      />
    </div>
  )
}

export default FacilityList
